import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useAuth } from '../context/AuthContext';
import {
  ChevronDown, ChevronUp, Info, Sparkles, Package, Copy, Plus, Eye
} from 'lucide-react';
import OptionEditorCard from './custom-options/OptionEditorCard';
import OptionPreviewDialog from './custom-options/OptionPreviewDialog';

/**
 * CustomOptionsConfig Component (Refactored)
 *
 * Key Changes:
 * 1. No redundant checkbox - parent controls visibility
 * 2. Vendors can create custom options even without templates
 * 3. Templates are starting points - fully editable, original template unaffected
 */
const CustomOptionsConfig = ({
  categoryId,
  subcategoryId,
  listingId,
  vendorId,
  basePrice = 0,
  onConfigChange,
  initialOptions
}) => {
  const { apiCall, user } = useAuth();

  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [customOptions, setCustomOptions] = useState(initialOptions || []);
  const [expanded, setExpanded] = useState(true);
  const [pricingConfig, setPricingConfig] = useState({ master_pricing_enabled: true });
  const [showPreview, setShowPreview] = useState(false);
  const initializedRef = useRef(initialOptions && initialOptions.length > 0);

  // ── Data fetching ────────────────────────────────────────────────────────

  const fetchPricingConfig = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (vendorId) params.append('vendor_id', vendorId);
      if (categoryId) params.append('category_id', categoryId);
      if (subcategoryId) params.append('subcategory_id', subcategoryId);
      const qs = params.toString();
      const config = await apiCall(`/api/product-option-templates/system/pricing-config${qs ? '?' + qs : ''}`);
      if (config) setPricingConfig(config);
    } catch {
      // fallback to default
    }
  }, [apiCall, vendorId, categoryId, subcategoryId]);

  const fetchTemplates = useCallback(async () => {
    if (!categoryId) return;
    setLoadingTemplates(true);
    try {
      const url = `/api/product-option-templates?category_id=${categoryId}${subcategoryId ? `&subcategory_id=${subcategoryId}` : ''}`;
      const data = await apiCall(url);
      setAvailableTemplates(data?.templates || data || []);
    } catch {
      setAvailableTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  }, [apiCall, categoryId, subcategoryId]);

  const fetchExistingConfig = useCallback(async () => {
    // Skip fetch if we already have options from initialOptions prop
    if (initializedRef.current) return;
    if (!listingId) return;
    try {
      const data = await apiCall(`/api/listings/${listingId}/product-options`);
      if (data?.options?.length) {
        setCustomOptions(data.options);
      }
    } catch {
      // ignore
    } finally {
      initializedRef.current = true;
    }
  }, [apiCall, listingId]);

  // Use a ref for onConfigChange to avoid re-triggering effects when parent re-renders
  const onConfigChangeRef = useRef(onConfigChange);
  onConfigChangeRef.current = onConfigChange;

  useEffect(() => { fetchTemplates(); fetchPricingConfig(); }, [fetchTemplates, fetchPricingConfig]);
  useEffect(() => { fetchExistingConfig(); }, [fetchExistingConfig]);
  useEffect(() => {
    if (onConfigChangeRef.current) {
      onConfigChangeRef.current({ options: customOptions, hasOptions: customOptions.length > 0 });
    }
  }, [customOptions]);

  // ── Option mutations ─────────────────────────────────────────────────────

  const applyTemplateAsStartingPoint = (template) => {
    // Templates store data as steps[].fields[], flatten into options
    const templateFields = [];
    if (template.steps?.length) {
      template.steps.forEach(step => {
        if (step.fields?.length) {
          step.fields.forEach(f => templateFields.push(f));
        }
      });
    }
    // Also support legacy flat options[] if present
    if (template.options?.length) {
      template.options.forEach(f => templateFields.push(f));
    }

    const newOptions = templateFields.map((templateOpt) => {
      const inputType = templateOpt.type || templateOpt.inputType || 'select';
      const hasChoices = ['select', 'radio', 'checkbox', 'dropdown', 'button-select', 'image-cards', 'gallery'].includes(inputType);
      return {
        id: `opt_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        name: templateOpt.label || templateOpt.name || '',
        helpText: templateOpt.help_text || templateOpt.helpText || templateOpt.description || '',
        type: inputType,
        required: templateOpt.required ?? true,
        priceImpact: templateOpt.price_impact ?? templateOpt.priceImpact ?? false,
        mechanism: templateOpt.default_mechanism || templateOpt.mechanism || 'addon',
        multiplierType: templateOpt.default_multiplier_type || templateOpt.multiplierType || 'fixed',
        choices: hasChoices
          ? (templateOpt.options || templateOpt.choices || []).map(c => ({
              label: c.label || c.name || '',
              value: c.value || '',
              price: c.default_price ?? c.price ?? 0
            }))
          : [{ label: '', value: '', price: 0 }],
        fromTemplate: template.name,
      };
    });
    setCustomOptions(prev => [...prev, ...newOptions]);
  };

  const addCustomOption = () => {
    setCustomOptions(prev => [...prev, {
      id: `opt_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      name: '',
      helpText: '',
      type: 'select',
      required: true,
      priceImpact: true,
      mechanism: 'addon',
      multiplierType: 'fixed',
      choices: [{ label: '', value: '', price: 0 }],
    }]);
  };

  const updateOption = (optionId, field, value) => {
    setCustomOptions(prev => prev.map(opt => opt.id === optionId ? { ...opt, [field]: value } : opt));
  };

  const updateChoice = (optionId, choiceIndex, field, value) => {
    setCustomOptions(prev => prev.map(opt => {
      if (opt.id !== optionId) return opt;
      const newChoices = [...opt.choices];
      newChoices[choiceIndex] = { ...newChoices[choiceIndex], [field]: value };
      return { ...opt, choices: newChoices };
    }));
  };

  const addChoice = (optionId) => {
    setCustomOptions(prev => prev.map(opt => {
      if (opt.id !== optionId) return opt;
      return { ...opt, choices: [...opt.choices, { label: '', value: '', price: 0 }] };
    }));
  };

  const removeChoice = (optionId, choiceIndex) => {
    setCustomOptions(prev => prev.map(opt => {
      if (opt.id !== optionId) return opt;
      return { ...opt, choices: opt.choices.filter((_, i) => i !== choiceIndex) };
    }));
  };

  const removeOption = (optionId) => {
    setCustomOptions(prev => prev.filter(opt => opt.id !== optionId));
  };

  // ── Render ───────────────────────────────────────────────────────────────

  if (loadingTemplates) {
    return (
      <div className="p-4 text-center text-gray-500">
        <div className="animate-spin inline-block w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full mb-2" />
        <p className="text-sm">Loading options...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="custom-options-config">
      {/* Header */}
      <div
        className="flex items-center justify-between cursor-pointer p-2 hover:bg-gray-50 rounded"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-blue-600" />
          <span className="font-medium">Custom Options</span>
          {customOptions.length > 0 && (
            <Badge variant="secondary" className="text-xs">
              {customOptions.length} option{customOptions.length > 1 ? 's' : ''}
            </Badge>
          )}
        </div>
        <Button variant="ghost" size="icon" className="h-6 w-6">
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>
      </div>

      {expanded && (
        <div className="space-y-4">
          {/* Templates */}
          {availableTemplates.length > 0 && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">Quick Start from Template</span>
              </div>
              <p className="text-xs text-blue-600 mb-3">Use a pre-configured template as a starting point. You can fully customize it after copying.</p>
              <div className="flex flex-wrap gap-2">
                {availableTemplates.map(tpl => (
                  <Button key={tpl.id} variant="outline" size="sm" className="bg-white" onClick={() => applyTemplateAsStartingPoint(tpl)}>
                    <Copy className="h-3 w-3 mr-1" />{tpl.name}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Pricing mode */}
          {customOptions.length > 0 && (
            <div className={`p-3 rounded-lg ${pricingConfig.master_pricing_enabled ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
              <div className="flex items-start gap-2">
                <Info className={`h-4 w-4 flex-shrink-0 mt-0.5 ${pricingConfig.master_pricing_enabled ? 'text-green-600' : 'text-amber-600'}`} />
                <div>
                  <span className={`text-sm font-medium ${pricingConfig.master_pricing_enabled ? 'text-green-900' : 'text-amber-900'}`}>
                    Pricing Mode: {pricingConfig.master_pricing_enabled ? 'Automatic' : 'Manual'}
                  </span>
                  <p className={`text-xs ${pricingConfig.master_pricing_enabled ? 'text-green-700' : 'text-amber-700'}`}>
                    {pricingConfig.master_pricing_enabled
                      ? 'Prices will automatically update on storefront based on customer selections.'
                      : pricingConfig.message || 'Final pricing will be collected separately (e.g., WhatsApp/call).'}
                  </p>
                  {pricingConfig.override_source && pricingConfig.override_source !== 'global_default' && (
                    <p className="text-xs text-gray-500 mt-1">
                      Source: {({ vendor: 'Vendor Setting', category: 'Category Setting', subcategory: 'Subcategory Setting' })[pricingConfig.override_source] || 'Global'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Option editors */}
          {customOptions.map((option) => (
            <OptionEditorCard
              key={option.id}
              option={option}
              onUpdate={updateOption}
              onUpdateChoice={updateChoice}
              onAddChoice={addChoice}
              onRemoveChoice={removeChoice}
              onRemove={removeOption}
            />
          ))}

          <Button variant="outline" className="w-full border-dashed" onClick={addCustomOption} data-testid="add-custom-option-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Custom Option
          </Button>

          {customOptions.length === 0 && (
            <div className="text-center py-6 text-gray-500">
              <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No product options configured yet</p>
              <p className="text-xs mt-1">
                {availableTemplates.length > 0 ? 'Start from a template above or add custom options' : 'Click "Add Custom Option" to create customization choices for your customers'}
              </p>
            </div>
          )}

          {customOptions.length > 0 && (
            <Button variant="secondary" className="w-full mt-4" onClick={() => setShowPreview(true)} data-testid="preview-options-btn">
              <Eye className="h-4 w-4 mr-2" /> Preview Customer View
            </Button>
          )}
        </div>
      )}

      <OptionPreviewDialog open={showPreview} onOpenChange={setShowPreview} options={customOptions} basePrice={basePrice} />
    </div>
  );
};

export default CustomOptionsConfig;
